import React from "react";
import { Button } from "@/components/ui/button";

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
};

const RELOAD_GUARD_KEY = "gt_recovery_reload_done";

function isTranslateDomError(error: unknown) {
  if (typeof error !== "object" || error === null) return false;
  const anyErr = error as { name?: unknown; message?: unknown };
  const name = typeof anyErr.name === "string" ? anyErr.name : "";
  const message = typeof anyErr.message === "string" ? anyErr.message : "";
  // Catch common DOM mutation errors from Google Translate
  return (
    (name === "NotFoundError" && message.includes("removeChild")) ||
    (name === "NotFoundError" && message.includes("insertBefore")) ||
    message.includes("Failed to execute 'removeChild'") ||
    message.includes("Failed to execute 'insertBefore'")
  );
}

/**
 * Google Translate can mutate the DOM which sometimes breaks React during route transitions.
 * This boundary recovers by doing automatic reload (EN only) instead of leaving a black/blank page.
 */
export default class TranslateRecoveryBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true } as State;
  }

  componentDidMount() {
    // Clear the reload guard on successful page load
    try {
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch {
      // Ignore storage errors
    }
  }

  componentDidCatch(error: unknown) {
    const lang = typeof window !== "undefined" ? localStorage.getItem("language") : null;
    const shouldRecover = lang === "en" && isTranslateDomError(error);

    if (!shouldRecover) return;

    try {
      if (!sessionStorage.getItem(RELOAD_GUARD_KEY)) {
        sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
        // Automatic reload for translate errors
        window.location.reload();
        return;
      }
    } catch {
      // If storage is blocked, just fall back to the error UI.
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    // Fallback UI only shown if automatic reload already happened
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-3">
          <h1 className="text-lg font-semibold">Pagina opnieuw laden</h1>
          <p className="text-sm text-muted-foreground">
            De vertaler kan soms de pagina verstoren bij navigeren. Klik op "Ververs" om verder te gaan.
          </p>
          <div className="flex justify-center">
            <Button onClick={() => window.location.reload()}>Ververs</Button>
          </div>
        </div>
      </div>
    );
  }
}
